import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import NavBar from "@/components/NavBar";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ArrowLeft, Bookmark, Heart, ShoppingBag } from "lucide-react";

export default function MemberCollectionDetailPage() {
  const { collectionId } = useParams();
  const [collection, setCollection] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/collections/${collectionId}`)
      .then(({ data }) => { setCollection(data.collection); setProducts(data.products || []); })
      .catch(() => setCollection(null))
      .finally(() => setLoading(false));
  }, [collectionId]);

  if (loading) return <><NavBar /><div className="p-10">Loading…</div></>;
  if (!collection) return <><NavBar /><div className="p-10">Collection not found.</div></>;

  return (
    <>
      <NavBar />
      <main className="max-w-6xl mx-auto px-5 sm:px-10 py-10">
        <Link
          to="/collections"
          data-testid="member-collection-back"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#7A7A7A] hover:text-[#1A422D]"
        >
          <ArrowLeft size={14} /> Back to collections
        </Link>

        <div className="mt-4" data-testid="member-collection-detail">
          <div className="overline">Collection</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#1A422D] mt-2">{collection.name}</h1>
          {collection.description && (
            <p className="mt-3 text-sm text-[#4A4A4A] max-w-2xl">{collection.description}</p>
          )}
        </div>

        {products.length === 0 ? (
          <div className="mt-10 p-8 border border-dashed border-[#B59A5A]/40 text-center text-sm text-[#7A7A7A]" data-testid="member-collection-empty">
            No products in this collection.
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="member-collection-products">
            {products.map((p) => (
              <Link
                key={p.product_id}
                to={`/collections/${collectionId}/products/${p.product_id}`}
                data-testid={`member-product-card-${p.product_id}`}
                className="block p-5 border border-[#E5E3D8] bg-white hover:border-[#B59A5A] transition-colors"
              >
                <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[#B59A5A]">
                  {p.availability_status || "available"}
                </div>
                <div className="mt-2 font-serif text-xl text-[#1A422D]">{p.product_name}</div>
                {p.description && (
                  <div className="mt-2 text-xs text-[#4A4A4A] line-clamp-2">{p.description}</div>
                )}
                {p.msrp != null && collection.display_pricing && (
                  <div className="mt-3 text-sm text-[#1A422D]">${p.msrp}</div>
                )}
                {p.msrp != null && !collection.display_pricing && (
                  <div className="mt-3 text-xs text-[#7A7A7A] italic">Price confirmed by your golf shop</div>
                )}
              </Link>
            ))}
          </div>
        )}
      </main>
    </>
  );
}

export function MemberProductDetailPage() {
  const { collectionId, productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [collectionMeta, setCollectionMeta] = useState(null);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [logoSelection, setLogoSelection] = useState("Club Crest");
  const [logoPlacement, setLogoPlacement] = useState("Left Chest");
  const [instructions, setInstructions] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([
        api.get(`/collections/${collectionId}/products/${productId}`),
        api.get(`/collections/${collectionId}`),
      ]);
      setProduct(p.data);
      setCollectionMeta(c.data.collection);
      setSize((p.data.sizes || [])[0] || "");
      setColor((p.data.colors || [])[0] || "");
    } catch (_e) {
      setProduct(null);
    }
  }, [collectionId, productId]);
  useEffect(() => { load(); }, [load]);

  const registerInterest = async (action) => {
    setFeedback(null);
    try {
      await api.post(`/collections/${collectionId}/products/${productId}/interest`, { action });
      setFeedback({ type: "success", text: action === "save" ? "Saved to your wishlist." : "Marked as interested." });
    } catch (err) {
      setFeedback({ type: "error", text: formatApiErrorDetail(err) || "Could not record your response." });
    }
  };

  const submitRequest = async () => {
    setSubmitting(true); setFeedback(null);
    try {
      const body = {
        size: size || undefined,
        color: color || undefined,
        quantity: Number(quantity) || 1,
        logo_selection: logoSelection || undefined,
        logo_placement: logoPlacement || undefined,
        special_instructions: instructions.trim() || undefined,
      };
      Object.keys(body).forEach((k) => { if (body[k] === undefined || body[k] === "") delete body[k]; });
      await api.post(`/collections/${collectionId}/products/${productId}/request`, body);
      setFeedback({ type: "success", text: "Request sent to your pro shop." });
      setTimeout(() => navigate("/collections"), 1500);
    } catch (err) {
      setFeedback({ type: "error", text: formatApiErrorDetail(err) || "Could not submit request." });
    } finally { setSubmitting(false); }
  };

  if (product === null) return <><NavBar /><div className="p-10">Product not found.</div></>;
  if (!product) return <><NavBar /><div className="p-10">Loading…</div></>;

  return (
    <>
      <NavBar />
      <main className="max-w-4xl mx-auto px-5 sm:px-10 py-10">
        <Link to={`/collections/${collectionId}`} data-testid="member-product-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#7A7A7A] hover:text-[#1A422D]">
          <ArrowLeft size={14} /> Back
        </Link>

        <div className="mt-4" data-testid="member-product-detail">
          <div className="overline">Product</div>
          <h1 className="font-serif text-4xl text-[#1A422D] mt-2">{product.product_name}</h1>
          {product.description && (
            <p className="mt-3 text-sm text-[#4A4A4A] max-w-2xl">{product.description}</p>
          )}
          {product.msrp != null && collectionMeta?.display_pricing && (
            <div className="mt-3 text-lg text-[#1A422D]">${product.msrp}</div>
          )}
          {product.msrp != null && !collectionMeta?.display_pricing && (
            <div className="mt-3 text-xs text-[#7A7A7A] italic">Price confirmed by your golf shop</div>
          )}

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {(product.sizes || []).length > 0 && (
              <div>
                <label className="label-elegant block mb-1">Size</label>
                <select value={size} onChange={(e) => setSize(e.target.value)} data-testid="member-product-size" className="input-field">
                  {product.sizes.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}
            {(product.colors || []).length > 0 && (
              <div>
                <label className="label-elegant block mb-1">Color</label>
                <select value={color} onChange={(e) => setColor(e.target.value)} data-testid="member-product-color" className="input-field">
                  {product.colors.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="label-elegant block mb-1">Quantity</label>
              <input type="number" min="1" max="99" value={quantity} onChange={(e) => setQuantity(e.target.value)} data-testid="member-product-qty" className="input-field" />
            </div>
            <div>
              <label className="label-elegant block mb-1">Logo</label>
              <select value={logoSelection} onChange={(e) => setLogoSelection(e.target.value)} data-testid="member-product-logo" className="input-field">
                <option>Club Crest</option>
                <option>Player Monogram</option>
                <option>No Logo</option>
              </select>
            </div>
            <div>
              <label className="label-elegant block mb-1">Logo Placement</label>
              <select value={logoPlacement} onChange={(e) => setLogoPlacement(e.target.value)} data-testid="member-product-logo-placement" className="input-field">
                <option>Left Chest</option>
                <option>Left Sleeve</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="label-elegant block mb-1">Special Instructions</label>
              <textarea rows={2} value={instructions} onChange={(e) => setInstructions(e.target.value)} data-testid="member-product-instructions" className="input-field" />
            </div>
          </div>

          {feedback && (
            <div className={`mt-4 text-sm ${feedback.type === "error" ? "text-[#a33]" : "text-[#1A422D]"}`} data-testid="member-product-feedback">
              {feedback.text}
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            {collectionMeta?.allow_wishlist !== false && (
              <button onClick={() => registerInterest("save")} data-testid="member-product-save" className="btn-secondary inline-flex items-center gap-2 text-xs">
                <Bookmark size={13} /> Save
              </button>
            )}
            {collectionMeta?.allow_interested !== false && (
              <button onClick={() => registerInterest("interested")} data-testid="member-product-interested" className="btn-secondary inline-flex items-center gap-2 text-xs">
                <Heart size={13} /> Interested
              </button>
            )}
            {collectionMeta?.allow_product_requests !== false && (
              <button onClick={submitRequest} disabled={submitting} data-testid="member-product-request" className="btn-primary inline-flex items-center gap-2">
                <ShoppingBag size={13} /> {submitting ? "Sending…" : "Request This Item"}
              </button>
            )}
          </div>
        </div>
      </main>
    </>
  );
}
